// src/services/erpService.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  documentId,
  Timestamp,
  serverTimestamp,
  setDoc
} from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";
import { construirResumenContextoFinanciero } from "../helpers/contextoFinanciero";
import { toJsDate } from "../helpers/fechas";
import { construirResumenFraccionamientos } from "../helpers/finanzasFraccionamiento";
import { construirResumenProyectos } from "../helpers/finanzasProyecto";
import { construirResumenLotes } from "../helpers/finanzasLotes";
import { construirValidacionesErp } from "../helpers/validacionesErp";
import { construirResumenCobranza } from "../helpers/resumenCobranza";
import {
  esPropiedadVendida,
  getPropiedadIdsDeuda,
  participaInventarioKye,
} from "../helpers/compatFinanciera";
import { normalizeMetodoPago } from "../helpers/metodosPago";

function parseMonthId(monthId) {
  // monthId: "YYYY-MM"
  const [y, m] = String(monthId).split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) throw new Error("monthId inválido. Usa YYYY-MM");
  const start = new Date(y, m - 1, 1, 0, 0, 0, 0);
  const end = new Date(y, m, 1, 0, 0, 0, 0);
  return { start, end };
}

function toDate(value) {
  return toJsDate(value);
}

function safeNumber(v) {
  if (v === null || v === undefined) return 0;

  // Si viene como string tipo "$80,000" o "80,000"
  if (typeof v === "string") {
    const cleaned = v.replace(/[^\d.-]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }

  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getPagosArray(pagos) {
  if (!pagos) return [];
  if (Array.isArray(pagos)) return pagos;
  if (typeof pagos === "object") return Object.values(pagos);
  return [];
}

function isInRange(date, start, end) {
  return date && date >= start && date < end;
}

function sameDay(a, b) {
  return a?.getTime?.() === b?.getTime?.();
}

function getMetodoMovimiento(mov = {}) {
  const metodo = normalizeMetodoPago(mov.metodo || mov.metodoEtiqueta || "");
  return {
    metodo: metodo.value,
    metodoEtiqueta: mov.metodoEtiqueta || metodo.label,
    esTransferenciaBancaria:
      mov.esTransferenciaBancaria === true || metodo.transferencia,
  };
}

function emptyMetodoResumen() {
  return {
    entradas: 0,
    salidas: 0,
    movimientos: 0,
    transferenciasSinReferencia: 0,
  };
}

function atMidnight(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

const ERP_EXTERNAL_SECTIONS = [
  "contextoFinanciero",
  "fraccionamientosFinancieros",
  "proyectosFinancieros",
  "lotesFinancieros",
  "cajaProyecto",
  "validacionesErp",
];

const FIRESTORE_SECTION_SAFE_BYTES = 760000;
const ERP_SCHEMA_VERSION = 4;

// Caps defensivos para queries del ERP — evitan facturas inesperadas si las colecciones crecen.
// Si una query alcanza el cap, se loguea una advertencia y la próxima iteración debe paginar.
const ERP_QUERY_CAPS = {
  propiedades: 20000,
  deudas: 20000,
  cxp_deudas: 20000,
  cajas_movimientos: 50000,
  obras: 5000,
  proyectos: 5000,
  fraccionamientos: 5000,
  cajas_clusters: 1000,
};

// Firestore rechaza limit() > 10000. Antes clampeábamos a 10000 (parche que
// dejaba de truncar solo mientras ninguna colección superara ese tope).
// ERP-R1 (confiabilidad): paginación real con cursor por documentId. Leemos en
// páginas de 10000 hasta agotar la colección o alcanzar el cap defensivo.
// Así el ERP escala más allá de 10000 docs/colección sin truncar ni romper.
const FIRESTORE_MAX_LIMIT = 10000;

// ERP-4: registro de truncamientos por recálculo. getDocsCapped empuja aquí
// cuando una colección alcanza su cap defensivo; recalcErpMonthly lo resetea al
// inicio y lo adjunta al payload para que la UI muestre un aviso VISIBLE
// (antes era solo un console.warn invisible). Es de módulo porque el recálculo
// es manual y secuencial (un corte a la vez).
let __erpTruncados = [];

async function getDocsCapped(queryOrCollection, cap, label) {
  const docsAcumulados = [];
  let cursor = null;
  let ultimaPagina = 0;

  do {
    const restante = cap - docsAcumulados.length;
    const tamPagina = Math.min(FIRESTORE_MAX_LIMIT, restante);
    const constraints = cursor
      ? [orderBy(documentId()), startAfter(cursor), limit(tamPagina)]
      : [orderBy(documentId()), limit(tamPagina)];
    const snap = await getDocs(query(queryOrCollection, ...constraints));
    ultimaPagina = snap.size;
    for (const d of snap.docs) docsAcumulados.push(d);
    cursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  } while (
    ultimaPagina === FIRESTORE_MAX_LIMIT &&
    docsAcumulados.length < cap &&
    cursor
  );

  if (docsAcumulados.length >= cap) {
    console.warn(
      `[ERP] ${label} alcanzó el cap defensivo (${cap} docs). Sube el cap en ERP_QUERY_CAPS si la colección creció legítimamente.`
    );
    __erpTruncados.push({ coleccion: label, cap, leidos: docsAcumulados.length });
  }

  // Devolvemos una forma compatible con QuerySnapshot para los callers
  // (usan .docs.map / .size / .forEach).
  return {
    docs: docsAcumulados,
    size: docsAcumulados.length,
    empty: docsAcumulados.length === 0,
    forEach: (cb) => docsAcumulados.forEach(cb),
  };
}

function approxBytes(value) {
  try {
    return new Blob([JSON.stringify(value ?? null)]).size;
  } catch {
    return JSON.stringify(value ?? null).length;
  }
}

function buildSlimSectionMarker(sectionKey, value) {
  return {
    resumen: value?.resumen || {},
    score: value?.score ?? null,
    externalized: true,
    section: sectionKey,
  };
}

function buildSlimErpPayload(payload = {}) {
  const slim = { ...payload };
  ERP_EXTERNAL_SECTIONS.forEach((sectionKey) => {
    if (payload[sectionKey] === undefined) return;
    slim[sectionKey] = buildSlimSectionMarker(sectionKey, payload[sectionKey]);
  });
  return slim;
}

function splitArrayForFirestore(items = [], maxBytes = FIRESTORE_SECTION_SAFE_BYTES) {
  const chunks = [];
  let current = [];
  let currentBytes = 2;

  items.forEach((item) => {
    const itemBytes = approxBytes(item) + 1;
    if (current.length && currentBytes + itemBytes > maxBytes) {
      chunks.push(current);
      current = [];
      currentBytes = 2;
    }
    current.push(item);
    currentBytes += itemBytes;
  });

  if (current.length) chunks.push(current);
  return chunks;
}

async function writeErpSection(monthId, sectionKey, value, { updatedBy } = {}) {
  const sectionRef = doc(db, "erp_stats_monthly", monthId, "sections", sectionKey);
  const bytes = approxBytes(value);

  if (bytes <= FIRESTORE_SECTION_SAFE_BYTES) {
    await setDoc(sectionRef, {
      kind: "object",
      section: sectionKey,
      data: value,
      bytes,
      updatedAt: serverTimestamp(),
      updatedBy: updatedBy || null,
    });
    return {
      section: sectionKey,
      kind: "object",
      bytes,
      chunks: 0,
      fieldsChunked: [],
    };
  }

  const base = {};
  const arrays = {};
  Object.entries(value || {}).forEach(([key, entryValue]) => {
    if (Array.isArray(entryValue)) {
      arrays[key] = entryValue;
    } else {
      base[key] = entryValue;
    }
  });

  const chunkRefs = {};
  const chunkWrites = [];
  Object.entries(arrays).forEach(([field, items]) => {
    const chunks = splitArrayForFirestore(items);
    chunkRefs[field] = chunks.map((_, index) => `${sectionKey}_${field}_${index}`);
    chunks.forEach((rows, index) => {
      const chunkId = `${sectionKey}_${field}_${index}`;
      chunkWrites.push(
        setDoc(doc(db, "erp_stats_monthly", monthId, "sectionChunks", chunkId), {
          section: sectionKey,
          field,
          index,
          rows,
          updatedAt: serverTimestamp(),
          updatedBy: updatedBy || null,
        })
      );
    });
  });

  await Promise.all(chunkWrites);
  await setDoc(sectionRef, {
    kind: "chunked",
    section: sectionKey,
    base,
    chunkRefs,
    bytes,
    updatedAt: serverTimestamp(),
    updatedBy: updatedBy || null,
  });

  return {
    section: sectionKey,
    kind: "chunked",
    bytes,
    chunks: Object.values(chunkRefs).reduce((acc, ids) => acc + (ids?.length || 0), 0),
    fieldsChunked: Object.keys(chunkRefs),
  };
}

async function writeErpExternalSections(monthId, payload, { updatedBy } = {}) {
  return Promise.all(
    ERP_EXTERNAL_SECTIONS
      .filter((sectionKey) => payload[sectionKey] !== undefined)
      .map((sectionKey) => writeErpSection(monthId, sectionKey, payload[sectionKey], { updatedBy }))
  );
}

function buildErpIntegrity(payload = {}, sectionWrites = []) {
  const written = new Set(sectionWrites.map((section) => section.section));
  const missingSections = ERP_EXTERNAL_SECTIONS.filter((sectionKey) => !written.has(sectionKey));
  const mainBytes = approxBytes(buildSlimErpPayload(payload));
  const sectionBytes = sectionWrites.reduce((acc, item) => acc + Number(item.bytes || 0), 0);
  return {
    complete: missingSections.length === 0 && mainBytes < 950000,
    schemaVersion: ERP_SCHEMA_VERSION,
    storageMode: "external_sections",
    requiredSections: ERP_EXTERNAL_SECTIONS,
    writtenSections: sectionWrites.map((item) => item.section),
    missingSections,
    mainDocBytes: mainBytes,
    sectionBytes,
    sections: sectionWrites,
    warnings: [
      ...(mainBytes >= 950000 ? ["main_doc_near_firestore_limit"] : []),
      ...missingSections.map((section) => `missing_${section}`),
    ],
  };
}

function attachErpReadIntegrity(payload = {}) {
  const existing = payload.recalcMeta?.integrity || payload.integrity || {};
  const missingSections = ERP_EXTERNAL_SECTIONS.filter((sectionKey) => {
    const value = payload[sectionKey];
    return !value || value.externalized === true;
  });
  return {
    ...payload,
    readIntegrity: {
      complete: missingSections.length === 0,
      missingSections,
      schemaVersion: payload.version || existing.schemaVersion || null,
      storageMode: payload.storageMode || existing.storageMode || "",
      mainDocBytes: existing.mainDocBytes || approxBytes(payload),
      warnings: [
        ...(existing.warnings || []),
        ...missingSections.map((section) => `section_not_hydrated_${section}`),
      ],
    },
  };
}

async function hydrateErpExternalSections(monthId, payload = {}) {
  const sectionsSnap = await getDocs(collection(db, "erp_stats_monthly", monthId, "sections"));
  if (sectionsSnap.empty) return payload;

  const hydrated = { ...payload };
  const chunkDocsById = new Map();
  const chunkedSections = sectionsSnap.docs
    .map((sectionDoc) => ({ id: sectionDoc.id, ...(sectionDoc.data() || {}) }))
    .filter((section) => section.kind === "chunked");

  if (chunkedSections.length) {
    const chunksSnap = await getDocs(collection(db, "erp_stats_monthly", monthId, "sectionChunks"));
    chunksSnap.forEach((chunkDoc) => {
      chunkDocsById.set(chunkDoc.id, chunkDoc.data() || {});
    });
  }

  sectionsSnap.forEach((sectionDoc) => {
    const section = sectionDoc.data() || {};
    const sectionKey = section.section || sectionDoc.id;

    if (section.kind === "object") {
      hydrated[sectionKey] = section.data || {};
      return;
    }

    if (section.kind === "chunked") {
      const rebuilt = { ...(section.base || {}) };
      Object.entries(section.chunkRefs || {}).forEach(([field, ids]) => {
        rebuilt[field] = (ids || []).flatMap((id) => chunkDocsById.get(id)?.rows || []);
      });
      hydrated[sectionKey] = rebuilt;
    }
  });

  return hydrated;
}

function compactarLotesFinancieros(lotesFinancieros = {}) {
  const costosPorOrigenRows = [];
  const rows = (lotesFinancieros.rows || []).map((row) => {
    Object.entries(row.costosPorOrigen || {}).forEach(([origen, item]) => {
      if (!safeNumber(item?.total)) return;
      costosPorOrigenRows.push({
        loteId: row.id || "",
        origen,
        label: item?.label || origen,
        count: item?.count || 0,
        total: safeNumber(item?.total),
      });
    });

    const { costosPorOrigen, ...rest } = row;
    return rest;
  });

  return {
    resumen: lotesFinancieros.resumen || {},
    rows,
    costosPorOrigenRows,
  };
}

function construirResumenCajaProyecto({ movimientosCaja = [], cxpItems = [], catalogos = {} } = {}) {
  const proyectosMap = catalogos.proyectos || new Map();
  const clusters = catalogos.cajasClusters || [];
  const porProyecto = new Map();
  const movimientosRows = [];
  const metodosMap = new Map();
  const general = {
    ingresosReales: 0,
    egresosReales: 0,
    transferenciasEntrada: 0,
    transferenciasSalida: 0,
    saldoCaja: 0,
    movimientos: 0,
    movimientosCancelados: 0,
  };

  const addMetodoResumen = (mov, monto, cancelado, transferenciaInterna) => {
    const metodoInfo = getMetodoMovimiento(mov);
    const key = metodoInfo.metodo || "sin_metodo";
    const current = metodosMap.get(key) || {
      metodo: key,
      metodoEtiqueta: metodoInfo.metodoEtiqueta || key,
      esTransferenciaBancaria: metodoInfo.esTransferenciaBancaria,
      ...emptyMetodoResumen(),
    };

    if (!cancelado && !transferenciaInterna) {
      if (mov.tipo === "entrada") current.entradas += monto;
      if (mov.tipo === "salida") current.salidas += monto;
    }
    if (!cancelado) current.movimientos += 1;
    if (
      !cancelado &&
      metodoInfo.esTransferenciaBancaria &&
      !String(mov.referencia || "").trim()
    ) {
      current.transferenciasSinReferencia += 1;
    }

    metodosMap.set(key, current);
    return metodoInfo;
  };

  const ensureProyecto = (id = "", nombre = "") => {
    const key = id || "sin_proyecto";
    if (!porProyecto.has(key)) {
      const proyecto = id ? proyectosMap.get(id) : null;
      porProyecto.set(key, {
        id: key,
        proyectoId: id,
        nombre: nombre || proyecto?.nombre || (id ? id : "Sin proyecto"),
        ingresosReales: 0,
        egresosReales: 0,
        transferenciasEntrada: 0,
        transferenciasSalida: 0,
        saldoCaja: 0,
        movimientos: 0,
        movimientosCancelados: 0,
        cxpPagadasDesdeCaja: 0,
        cxpPendientes: 0,
      });
    }
    return porProyecto.get(key);
  };

  movimientosCaja.forEach((mov) => {
    const proyectoId = mov.proyectoId || "";
    const row = ensureProyecto(proyectoId, mov.proyectoNombre || "");
    const cancelado = mov.cancelado === true || mov.activo === false;
    const monto = safeNumber(mov.monto);
    const transferenciaInterna = mov.transferenciaInterna === true || String(mov.origenTipo || "") === "transferencia_caja";
    const cajaScope = mov.cajaScope || (proyectoId ? "proyecto" : "");
    const metodoInfo = addMetodoResumen(mov, monto, cancelado, transferenciaInterna);

    movimientosRows.push({
      id: mov.id,
      fecha: mov.fecha || "",
      tipo: mov.tipo || "",
      monto,
      cajaScope,
      cajaId: mov.cajaId || "",
      transferenciaId: mov.transferenciaId || "",
      transferenciaInterna,
      metodo: metodoInfo.metodo,
      metodoEtiqueta: metodoInfo.metodoEtiqueta,
      esTransferenciaBancaria: metodoInfo.esTransferenciaBancaria,
      concepto: mov.concepto || "",
      proyectoId,
      proyectoNombre: row.nombre,
      cxpId: mov.cxpId || "",
      cxpPagoId: mov.cxpPagoId || "",
      referencia: mov.referencia || "",
      cancelado,
    });

    if (cancelado) {
      row.movimientosCancelados += 1;
      return;
    }

    if (cajaScope === "general") {
      general.movimientos += 1;
      if (transferenciaInterna && mov.tipo === "entrada") general.transferenciasEntrada += monto;
      else if (transferenciaInterna && mov.tipo === "salida") general.transferenciasSalida += monto;
      else if (mov.tipo === "entrada") general.ingresosReales += monto;
      else if (mov.tipo === "salida") general.egresosReales += monto;
      general.saldoCaja =
        general.ingresosReales +
        general.transferenciasEntrada -
        general.egresosReales -
        general.transferenciasSalida;
      return;
    }

    row.movimientos += 1;
    if (transferenciaInterna && mov.tipo === "entrada") row.transferenciasEntrada = safeNumber(row.transferenciasEntrada) + monto;
    else if (transferenciaInterna && mov.tipo === "salida") row.transferenciasSalida = safeNumber(row.transferenciasSalida) + monto;
    else if (mov.tipo === "entrada") row.ingresosReales += monto;
    else if (mov.tipo === "salida") row.egresosReales += monto;
    if (mov.cxpId && mov.tipo === "salida") row.cxpPagadasDesdeCaja += 1;
    row.saldoCaja =
      row.ingresosReales +
      safeNumber(row.transferenciasEntrada) -
      row.egresosReales -
      safeNumber(row.transferenciasSalida);
  });

  cxpItems.forEach((cxp) => {
    const saldo = safeNumber(cxp.saldoRestante);
    if (saldo <= 0 || cxp.activo === false || cxp.cancelada === true || cxp.estado === "cancelada") return;
    const row = ensureProyecto(cxp.proyectoId || "", cxp.proyectoNombre || "");
    row.cxpPendientes += saldo;
  });

  const rows = Array.from(porProyecto.values()).sort((a, b) => b.saldoCaja - a.saldoCaja);
  const resumen = rows.reduce(
    (acc, row) => {
      acc.ingresosReales += row.ingresosReales;
      acc.egresosReales += row.egresosReales;
      acc.transferenciasEntrada += safeNumber(row.transferenciasEntrada);
      acc.transferenciasSalida += safeNumber(row.transferenciasSalida);
      acc.saldoCaja += row.saldoCaja;
      acc.movimientos += row.movimientos;
      acc.movimientosCancelados += row.movimientosCancelados;
      acc.cxpPagadasDesdeCaja += row.cxpPagadasDesdeCaja;
      acc.cxpPendientes += row.cxpPendientes;
      return acc;
    },
    {
      ingresosReales: 0,
      egresosReales: 0,
      transferenciasEntrada: 0,
      transferenciasSalida: 0,
      saldoCaja: 0,
      movimientos: 0,
      movimientosCancelados: 0,
      cxpPagadasDesdeCaja: 0,
      cxpPendientes: 0,
    }
  );

  resumen.cajaGeneral = general;
  resumen.metodosPago = Array.from(metodosMap.values()).sort(
    (a, b) => (b.entradas + b.salidas) - (a.entradas + a.salidas)
  );
  resumen.transferenciasSinReferencia = resumen.metodosPago.reduce(
    (acc, row) => acc + safeNumber(row.transferenciasSinReferencia),
    0
  );

  const clusterRows = clusters.map((cluster) => {
    const ids = new Set(cluster.proyectoIds || []);
    const proyectos = rows.filter((row) => ids.has(row.proyectoId));
    return proyectos.reduce(
      (acc, row) => {
        acc.ingresosReales += row.ingresosReales;
        acc.egresosReales += row.egresosReales;
        acc.transferenciasEntrada += safeNumber(row.transferenciasEntrada);
        acc.transferenciasSalida += safeNumber(row.transferenciasSalida);
        acc.saldoCaja += row.saldoCaja;
        acc.movimientos += row.movimientos;
        acc.proyectos += 1;
        return acc;
      },
      {
        clusterId: cluster.id,
        nombre: cluster.nombre || cluster.id,
        ingresosReales: 0,
        egresosReales: 0,
        transferenciasEntrada: 0,
        transferenciasSalida: 0,
        saldoCaja: 0,
        movimientos: 0,
        proyectos: 0,
      }
    );
  });

  const conciliacionRows = cxpItems.map((cxp) => {
    const movimientos = movimientosRows.filter((mov) => mov.cxpId === cxp.id && !mov.cancelado);
    const pagadoCaja = movimientos.reduce((acc, mov) => acc + safeNumber(mov.monto), 0);
    const pagadoCxp = getPagosArray(cxp.pagos).reduce((acc, pago) => acc + safeNumber(pago?.cantidad), 0);
    return {
      cxpId: cxp.id,
      proveedor: cxp.proveedorNombre || cxp.proveedor || "",
      referencia: cxp.referencia || "",
      proyectoId: cxp.proyectoId || "",
      proyectoNombre: cxp.proyectoNombre || "",
      capitalizable: cxp.capitalizable === true,
      estado: cxp.estado || (cxp.activo === false ? "inactiva" : "activa"),
      saldoRestante: safeNumber(cxp.saldoRestante),
      pagadoCxp,
      pagadoCaja,
      movimientosCaja: movimientos.length,
      diferencia: pagadoCxp - pagadoCaja,
    };
  });

  return { resumen, rows, movimientosRows, conciliacionRows, clusterRows, general, metodosPagoRows: resumen.metodosPago };
}

function limpiarObraInactiva(item = {}, obrasActivas = new Map()) {
  if (!item.obraId || obrasActivas.has(item.obraId)) return item;
  return {
    ...item,
    obraId: "",
    obraNombre: "",
  };
}

export async function readErpMonthly(monthId) {
  const ref = doc(db, "erp_stats_monthly", monthId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const hydrated = await hydrateErpExternalSections(monthId, snap.data() || {});
  return attachErpReadIntegrity(hydrated);
}

export async function recalcErpMonthly(monthId, { updatedBy } = {}) {
  __erpTruncados = []; // ERP-4: limpiar registro de truncamientos de este corte
  const { start, end } = parseMonthId(monthId);
  const startTs = Timestamp.fromDate(start);
  const endTs = Timestamp.fromDate(end);

  // Hoy (para “vencidos / hoy / 7 días”)
  const hoy = atMidnight(new Date());
  const in7 = new Date(hoy);
  in7.setDate(in7.getDate() + 7);

  // fin del mes actual (para “vence este mes” de CxP)
  const finMesActual = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
  finMesActual.setHours(0, 0, 0, 0);

  // ===== Inventario (propiedades) =====
  const inventory = {
    disponiblesCount: 0,
    apartadasCount: 0,
    vendidasCount: 0,
    capitalDisponible: 0,
    reglaCapital: "precioContado"
  };

  let capitalInventario = 0;
  const propiedadesMap = new Map();
  const propiedadesItems = [];

  const propsSnap = await getDocsCapped(
    collection(db, "propiedades"),
    ERP_QUERY_CAPS.propiedades,
    "propiedades"
  );
  propsSnap.forEach((d) => {
    const p = { id: d.id, ...(d.data() || {}) };
    propiedadesMap.set(d.id, p);
    propiedadesItems.push(p);
    if (!participaInventarioKye(p)) return;
    const estado = String(p.estado || "").trim().toLowerCase();

    const esVendida = esPropiedadVendida(p);
    const esDisponible = !esVendida && (estado === "disponible" || p.disponible === true);
    const esApartada =
      !esVendida &&
      (
        estado === "apartado" ||
        estado === "apartada" ||
        estado === "reservada" ||
        estado === "reservado"
      );

    if (esDisponible) {
      // Regla: usamos precioContado y si falta, usamos precioFinanciado.
      const precio = safeNumber(p.precioContado ?? p.precioFinanciado ?? 0);

      inventory.disponiblesCount += 1;
      inventory.capitalDisponible += precio;
      capitalInventario += precio;
    } else if (esApartada) {
      inventory.apartadasCount += 1;
    } else if (esVendida) {
      inventory.vendidasCount += 1;
    }
  });

  // ===== CxP (cuentas por pagar) =====
  const cxp = {
    saldoRestanteTotal: 0,
    vencidoTotal: 0,
    venceHoyTotal: 0,
    vence7DiasTotal: 0,
    venceEsteMesTotal: 0
  };
  let cxpItems = [];
  let allCxpItems = [];

  const [obrasSnap, proyectosSnap, fraccionamientosSnap, cajasClustersSnap] = await Promise.all([
    getDocsCapped(collection(db, "obras"), ERP_QUERY_CAPS.obras, "obras"),
    getDocsCapped(collection(db, "proyectos"), ERP_QUERY_CAPS.proyectos, "proyectos"),
    getDocsCapped(collection(db, "fraccionamientos"), ERP_QUERY_CAPS.fraccionamientos, "fraccionamientos"),
    getDocsCapped(collection(db, "cajas_clusters"), ERP_QUERY_CAPS.cajas_clusters, "cajas_clusters").catch(() => ({ docs: [] })),
  ]);

  const obrasActivas = new Map(
    obrasSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((obra) => obra.activo !== false)
      .map((obra) => [obra.id, obra])
  );

  const catalogosFinancieros = {
    obras: obrasActivas,
    proyectos: new Map(proyectosSnap.docs.map((d) => [d.id, { id: d.id, ...d.data() }])),
    fraccionamientos: new Map(fraccionamientosSnap.docs.map((d) => [d.id, { id: d.id, ...d.data() }])),
    propiedades: propiedadesMap,
    cajasClusters: cajasClustersSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter((item) => item.activo !== false),
  };

  const allCxpSnap = await getDocsCapped(
    collection(db, "cxp_deudas"),
    ERP_QUERY_CAPS.cxp_deudas,
    "cxp_deudas (all)"
  );
  allCxpItems = allCxpSnap.docs.map((d) =>
    limpiarObraInactiva({ id: d.id, ...(d.data() || {}) }, obrasActivas)
  );

  const cajaSnap = await getDocsCapped(
    collection(db, "cajas_movimientos"),
    ERP_QUERY_CAPS.cajas_movimientos,
    "cajas_movimientos"
  );
  const movimientosCaja = cajaSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

  try {
    const cxpQ = query(
      collection(db, "cxp_deudas"),
      where("activo", "==", true),
      limit(Math.min(ERP_QUERY_CAPS.cxp_deudas, FIRESTORE_MAX_LIMIT))
    );
    const cxpSnap = await getDocs(cxpQ);
    if (cxpSnap.size >= ERP_QUERY_CAPS.cxp_deudas) {
      console.warn(
        `[ERP] cxp_deudas activas alcanzó el cap (${ERP_QUERY_CAPS.cxp_deudas}). Considera paginar.`
      );
    }
    cxpSnap.forEach((d) => {
      const x = limpiarObraInactiva({ id: d.id, ...(d.data() || {}) }, obrasActivas);
      cxpItems.push(x);
      const saldo = safeNumber(x.saldoRestante);
      cxp.saldoRestanteTotal += saldo;

      const due = toDate(x.nextDueDate);
      if (!due) return;
      const dueDay = atMidnight(due);

      if (dueDay < hoy) cxp.vencidoTotal += saldo;
      if (sameDay(dueDay, hoy)) cxp.venceHoyTotal += saldo;
      if (dueDay >= hoy && dueDay < in7) cxp.vence7DiasTotal += saldo;
      if (dueDay >= hoy && dueDay < finMesActual) cxp.venceEsteMesTotal += saldo;
    });
  } catch (e) {
    // fallback
    const cxpSnap = await getDocsCapped(
      collection(db, "cxp_deudas"),
      ERP_QUERY_CAPS.cxp_deudas,
      "cxp_deudas (fallback)"
    );
    cxpSnap.forEach((d) => {
      const x = limpiarObraInactiva({ id: d.id, ...(d.data() || {}) }, obrasActivas);
      if (x.activo !== true) return;
      cxpItems.push(x);

      const saldo = safeNumber(x.saldoRestante);
      cxp.saldoRestanteTotal += saldo;

      const due = toDate(x.nextDueDate);
      if (!due) return;
      const dueDay = atMidnight(due);

      if (dueDay < hoy) cxp.vencidoTotal += saldo;
      if (sameDay(dueDay, hoy)) cxp.venceHoyTotal += saldo;
      if (dueDay >= hoy && dueDay < in7) cxp.vence7DiasTotal += saldo;
      if (dueDay >= hoy && dueDay < finMesActual) cxp.venceEsteMesTotal += saldo;
    });
  }

  const contextoFinanciero = construirResumenContextoFinanciero(
    cxpItems,
    catalogosFinancieros
  );

  // ===== Deudas (CxC + ventas + cobranza) =====
  let ventasMes = 0;
  let ventasSinPropiedadMes = 0;
  const propiedadesVendidasMes = new Set();
  let cobranzaMes = 0;
  let numPagosMes = 0;
  const deudasItems = [];

  const cxc = {
    saldoPorCobrar: 0,
    programadoCobrarMes: 0,
    vencidoCalendario: 0,
    vencidoCalendarioLegacy: 0,
    moraReal: 0,
    cuentasEnMoraReal: 0,
    cuentasCriticas: 0,
    potencialRecuperable: 0,
    porCobrarHoy: 0,
    porCobrarSemana: 0,
    saldoPorCobrarReal: 0
  };

  const deudasSnap = await getDocsCapped(
    collection(db, "deudas"),
    ERP_QUERY_CAPS.deudas,
    "deudas"
  );
  deudasSnap.forEach((d) => {
    const deuda = { id: d.id, ...(d.data() || {}) };
    deudasItems.push(deuda);

    // Ventas del mes: lotes vendidos por contratos creados en el mes.
    const creada = toDate(deuda.creada);
    if (isInRange(creada, start, end)) {
      const propiedadIds = getPropiedadIdsDeuda(deuda);

      if (propiedadIds.length) {
        propiedadIds.forEach((propiedadId) => {
          const propiedad = propiedadesMap.get(propiedadId);
          if (!propiedad) {
            propiedadesVendidasMes.add(propiedadId);
            return;
          }
          if (participaInventarioKye(propiedad) && esPropiedadVendida(propiedad)) {
            propiedadesVendidasMes.add(propiedadId);
          }
        });
      } else {
        ventasSinPropiedadMes += 1;
      }
    }

    // Activa (compat)
    const estatus = deuda.estatus || deuda.estado;
    const activa = deuda.activo === true || estatus === "activa";

    if (activa) {
      cxc.saldoPorCobrar += safeNumber(deuda.saldoRestante ?? deuda.saldo);

      // Programado (por nextDueDate + mensualidad)
      const due = toDate(deuda.nextDueDate);
      if (due) {
        const dueDay = atMidnight(due);
        const mensualidad = safeNumber(deuda.mensualidad ?? deuda.pagoMensual);

        if (isInRange(dueDay, start, end)) {
          cxc.programadoCobrarMes += mensualidad;
        }
        if (dueDay < hoy) cxc.vencidoCalendarioLegacy += mensualidad;
      }
    }

    // Cobranza (pagos positivos dentro del mes)
    const pagos = getPagosArray(deuda.pagos);
    for (const p of pagos) {
      const cantidad = safeNumber(p?.cantidad);
      if (cantidad <= 0) continue;
      const f = toDate(p?.fecha);
      if (!isInRange(f, start, end)) continue;

      cobranzaMes += cantidad;
      numPagosMes += 1;
    }
  });

  ventasMes = propiedadesVendidasMes.size + ventasSinPropiedadMes;

  const cobranzaReal = construirResumenCobranza(deudasSnap.docs, hoy);
  cxc.saldoPorCobrarReal = safeNumber(cobranzaReal.resumen.saldoPendiente);
  cxc.moraReal = safeNumber(cobranzaReal.resumen.totalMoraReal);
  cxc.vencidoCalendario = cxc.moraReal;
  cxc.cuentasEnMoraReal = safeNumber(cobranzaReal.resumen.cuentasEnMora);
  cxc.cuentasCriticas = safeNumber(cobranzaReal.resumen.cuentasCriticas);
  cxc.potencialRecuperable = safeNumber(cobranzaReal.resumen.potencialRecuperable);
  cxc.porCobrarHoy = safeNumber(cobranzaReal.resumen.porCobrarHoy);
  cxc.porCobrarSemana = safeNumber(cobranzaReal.resumen.porCobrarSemana);
  // ERP-R4: buckets de aging de cartera (vigente / 1-30 / 31-60 / 61-90 / +90d
  // mapeados a meses de atraso). Calculados en el motor de cobranza.
  cxc.aging = cobranzaReal.resumen.aging || null;

  const ticketPromedio = numPagosMes > 0 ? (cobranzaMes / numPagosMes) : 0;
  const brechaMes = Math.max(0, safeNumber(cxc.programadoCobrarMes) - safeNumber(cobranzaMes));
  const eficienciaCobranza =
    cxc.programadoCobrarMes > 0 ? (cobranzaMes / cxc.programadoCobrarMes) : null;

  // ERP-1 (2026-06-17): excluir CxP CANCELADAS del lado de costos. Una CxP
  // cancelada ya no es un compromiso y NO debe restar utilidad. Antes se
  // pasaba allCxpItems completo → una CxP cancelada capitalizable subestimaba
  // la utilidad del proyecto/propiedad (asimetria: el ingreso/deuda SI filtra
  // cancelacion, el costo NO).
  // OJO: NO filtramos por saldo — una CxP PAGADA (saldo 0) sigue siendo un
  // costo real incurrido y debe contar. Solo se excluye lo cancelado/inactivo.
  const esCxpCancelada = (c) =>
    c?.activo === false || c?.cancelada === true || c?.estado === "cancelada";
  const cxpVigentes = allCxpItems.filter((c) => !esCxpCancelada(c));

  // ERP-R6: comisiones devengadas vs pagadas. Las comisiones de venta son CxP
  // con tipoCosto "comision_venta". Devengado = importe total de la CxP (la
  // comisión reconocida); pendiente = saldoRestante; pagado = devengado −
  // pendiente. Excluimos canceladas. Cierra el gap del roadmap (hoy no se veía
  // cuánto de lo devengado ya se pagó al comisionista).
  const comisionesCxp = cxpVigentes.filter(
    (c) => c?.tipoCosto === "comision_venta"
  );
  const comisionesDevengado = comisionesCxp.reduce(
    (s, c) => s + safeNumber(c.importe),
    0
  );
  const comisionesPendiente = comisionesCxp.reduce(
    (s, c) => s + safeNumber(c.saldoRestante),
    0
  );
  cxp.comisiones = {
    devengado: comisionesDevengado,
    pagado: Math.max(0, comisionesDevengado - comisionesPendiente),
    pendiente: comisionesPendiente,
    cuentas: comisionesCxp.length,
  };

  const fraccionamientosFinancieros = construirResumenFraccionamientos({
    propiedades: propiedadesItems,
    deudas: deudasItems,
    gastos: cxpVigentes,
    catalogos: catalogosFinancieros,
  });
  const proyectosFinancieros = construirResumenProyectos({
    propiedades: propiedadesItems,
    deudas: deudasItems,
    gastos: cxpVigentes,
    catalogos: catalogosFinancieros,
  });
  const lotesFinancieros = construirResumenLotes({
    propiedades: propiedadesItems,
    deudas: deudasItems,
    gastos: cxpVigentes,
    catalogos: catalogosFinancieros,
  });
  const lotesFinancierosCompactos = compactarLotesFinancieros(lotesFinancieros);
  const cajaProyecto = construirResumenCajaProyecto({
    movimientosCaja,
    cxpItems: allCxpItems,
    catalogos: catalogosFinancieros,
  });
  const validacionesErp = construirValidacionesErp({
    propiedades: propiedadesItems,
    deudas: deudasItems,
    cxpItems: allCxpItems,
    movimientosCaja,
    lotesFinancieros: lotesFinancierosCompactos,
    catalogos: catalogosFinancieros,
  });

  // ===== Guardar corte mensual =====
  const payloadBase = {
    month: monthId,
    from: startTs,
    to: endTs,

    kpis: {
      cobranzaMes,
      ventasMes,
      capitalInventario,
      cxpVencido: cxp.vencidoTotal,
      saldoPorCobrar: cxc.saldoPorCobrar,
      saldoPorCobrarReal: cxc.saldoPorCobrarReal,
      moraReal: cxc.moraReal,
      cuentasEnMoraReal: cxc.cuentasEnMoraReal,
      cuentasCriticas: cxc.cuentasCriticas,
      potencialRecuperable: cxc.potencialRecuperable,
      porCobrarHoy: cxc.porCobrarHoy,
      porCobrarSemana: cxc.porCobrarSemana,
      ticketPromedio,
      numPagosMes,
      programadoCobrarMes: cxc.programadoCobrarMes,
      brechaMes,
      eficienciaCobranza
    },

    inventory,
    cxp,
    contextoFinanciero,
    fraccionamientosFinancieros,
    proyectosFinancieros,
    lotesFinancieros: lotesFinancierosCompactos,
    cajaProyecto,
    validacionesErp,
    cxc: {
      ...cxc,
      brechaMes,
      eficienciaCobranza
    },

    updatedAt: serverTimestamp(),
    updatedBy: updatedBy || null,
    recalculatedAtIso: new Date().toISOString(),
    version: ERP_SCHEMA_VERSION,
    storageMode: "external_sections",
    // ERP-4: colecciones que alcanzaron su cap defensivo en este recálculo.
    // La UI muestra un aviso si esto no está vacío (los números podrían estar
    // incompletos y hay que subir el cap en ERP_QUERY_CAPS).
    cargasMeta: { truncado: [...__erpTruncados] }
  };

  const sectionWrites = await writeErpExternalSections(monthId, payloadBase, { updatedBy });
  const integrity = buildErpIntegrity(payloadBase, sectionWrites);
  const payload = {
    ...payloadBase,
    recalcMeta: {
      monthId,
      updatedBy: updatedBy || null,
      recalculatedAtIso: payloadBase.recalculatedAtIso,
      integrity,
    },
  };

  await setDoc(doc(db, "erp_stats_monthly", monthId), buildSlimErpPayload(payload));
  await addDoc(collection(db, "erp_stats_monthly", monthId, "recalc_log"), {
    monthId,
    updatedBy: updatedBy || null,
    recalculatedAtIso: payloadBase.recalculatedAtIso,
    createdAt: serverTimestamp(),
    integrity,
    kpis: payload.kpis,
  });
  return payload;
}

export async function recalcErpKeyMonths(monthId, { monthsBack = 3, updatedBy } = {}) {
  const [y, m] = String(monthId).split("-").map(Number);
  if (!y || !m) throw new Error("monthId invalido. Usa YYYY-MM");
  const base = new Date(y, m - 1, 1, 0, 0, 0, 0);
  const ids = Array.from({ length: Math.max(1, Number(monthsBack || 1)) }).map((_, index) => {
    const d = new Date(base);
    d.setMonth(base.getMonth() - index);
    return monthIdFromDate(d);
  });

  const results = [];
  for (const id of ids) {
    const data = await recalcErpMonthly(id, { updatedBy });
    results.push({
      monthId: id,
      complete: data.recalcMeta?.integrity?.complete === true,
      missingSections: data.recalcMeta?.integrity?.missingSections || [],
      mainDocBytes: data.recalcMeta?.integrity?.mainDocBytes || 0,
      warnings: data.recalcMeta?.integrity?.warnings || [],
    });
  }

  return {
    monthId,
    monthsBack: ids.length,
    results,
    complete: results.every((row) => row.complete),
  };
}
// Helpers para meses
function monthIdFromDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function addMonths(date, delta) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + delta);
  return d;
}

/**
 * Lee N meses hacia atrás incluyendo el mesId dado.
 * Devuelve array ordenado de viejo -> nuevo:
 * [{ monthId, cobranzaMes, ventasMes, programadoCobrarMes }, ...]
 */
export async function readErpLastMonths(monthId, monthsBack = 12) {
  const [y, m] = String(monthId).split("-").map(Number);
  if (!y || !m) throw new Error("monthId inválido. Usa YYYY-MM");

  const base = new Date(y, m - 1, 1, 0, 0, 0, 0);

  const ids = Array.from({ length: monthsBack }).map((_, i) => {
    const d = addMonths(base, -(monthsBack - 1 - i));
    return monthIdFromDate(d);
  });

  const docs = await Promise.all(
    ids.map(async (id) => {
      const snap = await getDoc(doc(db, "erp_stats_monthly", id));
      if (!snap.exists()) {
        return {
          monthId: id,
          cobranzaMes: 0,
          ventasMes: 0,
          programadoCobrarMes: 0
        };
      }
      const data = snap.data() || {};
      const k = data.kpis || {};
      return {
        monthId: id,
        cobranzaMes: Number(k.cobranzaMes || 0),
        ventasMes: Number(k.ventasMes || 0),
        programadoCobrarMes: Number(k.programadoCobrarMes || 0)
      };
    })
  );

  return docs;
}
